import React, { useRef, useState } from "react";
import { ImagePlus, Link2, Trash2, UserRound } from "lucide-react";
import { AppButton, FieldError, IconButton, Input, cn } from "../../ui";
import { uploadCmsImage } from "../../../utils/cmsAdminApi";

const MAX_BYTES = 3 * 1024 * 1024;
const ACCEPT = "image/png,image/jpeg,image/webp";

/**
 * ImageField — an image for the event page: uploaded (stored as a key the
 * backend turns into a fresh URL on every read) or, failing that, a pasted link.
 *
 * Uploads go through the website's image endpoint, which is part of the
 * website plan. An institute without it — which cannot publish the page
 * anyway — gets the link box and a sentence saying why.
 *
 * Props:
 *   previewUrl — what to show now (a resolved upload, or the typed link)
 *   linkUrl    — the typed link, when there is no upload
 *   onUploaded — ({ key, url }) after a successful upload
 *   onLink     — (url) when a link is typed
 *   onClear    — remove the image
 *   shape      — "wide" (a 16:9 cover) | "round" (a speaker photo)
 *   label      — accessible name for the upload button
 */
const ImageField = ({ previewUrl, linkUrl = "", onUploaded, onLink, onClear, shape = "wide", label = "Upload image" }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showLink, setShowLink] = useState(Boolean(linkUrl) && !previewUrl);
  const round = shape === "round";

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) { setError("Please choose an image under 3 MB."); return; }
    setBusy(true);
    setError("");
    try {
      const res = await uploadCmsImage(file, "page-media");
      if (!res?.key) throw new Error(res?.message || "Upload failed");
      onUploaded({ key: res.key, url: res.url });
      setShowLink(false);
    } catch (err) {
      setError(err?.response?.status === 403
        ? "Image uploads come with the website plan. Paste a link to the image instead."
        : (err?.response?.data?.message || "Upload failed. Please try again."));
      setShowLink(true);
    }
    setBusy(false);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-flex tw-items-center tw-gap-3">
        <div
          className={cn(
            "tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-border tw-border-line tw-bg-surface-sunken tw-text-content-subtle",
            round ? "tw-h-16 tw-w-16 tw-rounded-full" : "tw-h-20 tw-w-36 tw-rounded-md",
          )}
        >
          {previewUrl
            ? <img src={previewUrl} alt="" className="tw-h-full tw-w-full tw-object-cover" />
            : (round ? <UserRound size={24} /> : <ImagePlus size={22} />)}
        </div>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <input ref={fileRef} type="file" accept={ACCEPT} className="tw-hidden" onChange={pick} />
          <AppButton type="button" size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()}>
            <ImagePlus size={16} /> {busy ? "Uploading…" : (previewUrl ? "Replace" : label)}
          </AppButton>
          {!showLink && !previewUrl && (
            <AppButton type="button" size="sm" variant="ghost" onClick={() => setShowLink(true)}>
              <Link2 size={16} /> Use a link
            </AppButton>
          )}
          {previewUrl && <IconButton type="button" label="Remove image" icon={Trash2} size="sm" variant="ghost-destructive" onClick={onClear} />}
        </div>
      </div>
      {showLink && (
        <Input
          type="url"
          size="sm"
          value={linkUrl}
          placeholder="https://…/image.jpg"
          aria-label="Image link"
          onChange={(e) => onLink(e.target.value)}
        />
      )}
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
};

export default ImageField;
